import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Award, ExternalLink, Mail } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { type PlanAction, PlanPickerCard } from "@/components/app/plan-picker-card";
import { SubscriptionBanners } from "@/components/app/subscription-banner";
import { UsageMeter } from "@/components/app/usage-meter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { apiFetch, assignIfStripe } from "@/lib/api";
import { billingQueries } from "@/lib/queries";
import { CheckoutResponseSchema, PortalResponseSchema } from "@/lib/schemas";
import { formatBytes } from "@/lib/utils";

export const Route = createFileRoute("/_authed/settings/billing")({
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(billingQueries.subscription()),
      context.queryClient.ensureQueryData(billingQueries.plans()),
    ]),
  component: BillingPage,
});

function BillingPage() {
  const queryClient = useQueryClient();
  const { data: subscription } = useSuspenseQuery(billingQueries.subscription());
  const { data: plansResponse } = useSuspenseQuery(billingQueries.plans());
  const plans = plansResponse.plans;

  const [interval, setInterval] = useState<"month" | "year">("month");

  const billingURL = `${window.location.origin}/settings/billing`;

  const checkout = useMutation({
    mutationFn: ({ plan, interval }: { plan: string; interval: "month" | "year" }) =>
      apiFetch("/billing/checkout", CheckoutResponseSchema, {
        method: "POST",
        body: JSON.stringify({
          plan,
          interval,
          success_url: `${billingURL}?checkout=success`,
          cancel_url: `${billingURL}?checkout=canceled`,
        }),
      }),
    onSuccess: ({ url }) => {
      // Mark billing queries stale BEFORE the redirect so post-Stripe return
      // refetches. invalidateQueries only marks stale; it does not await any
      // refetch, so this is fire-and-forget and safe before window.location.
      queryClient.invalidateQueries({ queryKey: billingQueries.all });
      if (!assignIfStripe(url)) {
        toast.error("Invalid checkout URL");
      }
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Checkout failed"),
  });

  const portal = useMutation({
    mutationFn: () =>
      apiFetch("/billing/portal", PortalResponseSchema, {
        method: "POST",
        body: JSON.stringify({ return_url: billingURL }),
      }),
    onSuccess: ({ url }) => {
      if (!assignIfStripe(url)) {
        toast.error("Invalid billing portal URL");
      }
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Portal failed"),
  });

  const handleAction = (action: PlanAction) => {
    if (action.kind === "portal") portal.mutate();
    else checkout.mutate({ plan: action.plan, interval: action.interval });
  };

  const tier = subscription.tier;
  const isPaidTier = tier !== "free";
  const canCancel = isPaidTier && !!subscription.stripe_customer_id;
  const pending = checkout.isPending || portal.isPending;

  return (
    <section className="mx-auto max-w-5xl space-y-6">
      <h1 className="font-display text-2xl">Billing</h1>

      <SubscriptionBanners
        subscription={subscription}
        onPortal={() => portal.mutate()}
        portalPending={portal.isPending}
      />

      <Card className="p-6">
        <div className="mb-3 flex items-center justify-between">
          <span className="font-label text-[10px] uppercase tracking-widest text-muted-foreground">
            Current plan
          </span>
          {subscription.billing_enabled && subscription.stripe_customer_id && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => portal.mutate()}
              disabled={portal.isPending}
            >
              <ExternalLink className="size-3" /> Manage billing
            </Button>
          )}
        </div>

        <div className="mb-5 flex items-center gap-2">
          <span
            className={
              isPaidTier
                ? "font-display text-2xl capitalize text-primary"
                : "font-display text-2xl capitalize text-muted-foreground"
            }
          >
            {tier}
          </span>
          {isPaidTier && <Award className="size-5 text-primary/60" />}
        </div>

        <div className="space-y-4">
          <UsageMeter
            label="Sites"
            used={subscription.site_count}
            limit={subscription.site_limit}
            overLimitMessage="Approaching plan limit. Consider upgrading."
          />
          <UsageMeter
            label="Storage"
            used={subscription.storage_bytes_used}
            limit={subscription.storage_bytes_limit}
            format={formatBytes}
            overLimitMessage="Storage nearly full — consider upgrading your plan."
          />
          <UsageMeter
            label="Bandwidth (this month)"
            used={subscription.bandwidth_used}
            limit={subscription.bandwidth_limit}
            format={formatBytes}
            overLimitMessage="Bandwidth nearly exhausted — consider upgrading your plan."
          />
        </div>
      </Card>

      {subscription.billing_enabled && plans.length > 0 && (
        <Card className="p-6">
          <div className="mb-5 flex items-center justify-between gap-3">
            <h3 className="font-display text-sm">
              {isPaidTier ? "Change plan" : "Upgrade your plan"}
            </h3>
            <RadioGroup
              aria-label="Billing interval"
              value={interval}
              onValueChange={(v) => setInterval(v as "month" | "year")}
              className="inline-flex items-center gap-1 rounded-md border border-border bg-secondary p-1"
            >
              <RadioOption value="month" current={interval}>
                Monthly
              </RadioOption>
              <RadioOption value="year" current={interval}>
                Yearly
              </RadioOption>
            </RadioGroup>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {plans.map((plan) => (
              <PlanPickerCard
                key={plan.id}
                plan={plan}
                currentTier={tier}
                interval={interval}
                pending={pending}
                canCancel={canCancel}
                onAction={handleAction}
              />
            ))}
          </div>

          <p className="mt-4 font-mono text-xs text-muted-foreground">
            Need more?{" "}
            <a href="mailto:hello@sandbar.cloud?subject=Enterprise%20Plan" className="text-primary">
              Contact us
            </a>{" "}
            for Enterprise.
          </p>
        </Card>
      )}

      {!subscription.billing_enabled && (
        <Card className="p-6 text-center">
          <p className="mb-3 text-sm text-muted-foreground">
            Self-serve billing is not configured for this environment.
          </p>
          <Button asChild variant="outline" size="sm">
            <a href="mailto:hello@sandbar.cloud?subject=Plan%20Upgrade">
              <Mail className="size-3" /> Contact us
            </a>
          </Button>
        </Card>
      )}
    </section>
  );
}

function RadioOption({
  value,
  current,
  children,
}: {
  value: "month" | "year";
  current: "month" | "year";
  children: React.ReactNode;
}) {
  const active = current === value;
  return (
    <label
      htmlFor={`billing-cycle-${value}`}
      className={
        active
          ? "inline-flex cursor-pointer items-center gap-2 rounded-sm bg-background px-3 py-1 text-sm shadow-sm has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring"
          : "inline-flex cursor-pointer items-center gap-2 rounded-sm px-3 py-1 text-sm text-muted-foreground has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring"
      }
    >
      <RadioGroupItem id={`billing-cycle-${value}`} value={value} className="sr-only" />
      <span>{children}</span>
    </label>
  );
}
